import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Calculator, Search, FileText, Globe, Map, SpellCheck2, 
  Shield, Building2, MapPin, PenTool, MessageSquare, Image, 
  Code, Brain, Bot, Wand2, Sparkles, ArrowRight, Star, Zap,
  ExternalLink, Crown, Rocket, TrendingUp, CheckCircle, Lock,
  Filter, Grid3X3, LayoutList, ChevronRight
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { BorderBeam } from "@/components/ui/border-beam";
import { FloatingOrb, GridPattern } from "@/components/ui/decorative-elements";

interface ToolsHubProps {
  language: 'es' | 'en';
}

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: any;
  features: string[];
  premium: boolean;
  external?: boolean;
  url?: string;
  category: string;
  gradient: string;
  popularity?: number;
  isNew?: boolean;
  isFeatured?: boolean;
}

export const ToolsHub = ({ language }: ToolsHubProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'seo' | 'ai'>('seo');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const content = {
    es: {
      hero: {
        badge: "Suite Profesional de Herramientas",
        title: "Potencia tu Estrategia Digital",
        subtitle: "Herramientas SEO y IA de nivel empresarial para dominar los buscadores",
        stats: [
          { value: "15", label: "Herramientas" },
          { value: "50K+", label: "Análisis realizados" },
          { value: "98%", label: "Satisfacción" }
        ]
      },
      tabs: {
        seo: { label: "SEO Tools", icon: Search, count: 10 },
        ai: { label: "AI Tools", icon: Sparkles, count: 10 }
      },
      categories: {
        all: "Todas",
        analysis: "Análisis",
        optimization: "Optimización",
        local: "SEO Local",
        content: "Contenido",
        external: "Externas"
      },
      search: "Buscar herramientas...",
      labels: {
        features: "Características",
        premium: "Premium",
        free: "Gratuito",
        external: "Externa",
        useTool: "Usar Herramienta",
        visitTool: "Visitar",
        comingSoon: "Próximamente",
        new: "Nuevo",
        featured: "Destacado",
        popular: "Popular"
      },
      seoTools: [
        {
          id: "seo-mega-tool",
          title: "SEO MEGA TOOL",
          description: "Auditoría SEO completa con análisis competitivo, Core Web Vitals y backlinks",
          icon: Rocket,
          features: ["Auditoría completa", "Análisis competitivo", "Core Web Vitals", "Backlinks"],
          premium: false,
          category: "analysis",
          gradient: "from-brand via-red-500 to-orange-500",
          popularity: 98,
          isFeatured: false
        },
        {
          id: "contador-palabras",
          title: "Contador de Palabras",
          description: "Análisis detallado con métricas de legibilidad y tiempo de lectura",
          icon: Calculator,
          features: ["Análisis legibilidad", "Tiempo lectura", "Densidad palabras"],
          premium: false,
          category: "content",
          gradient: "from-blue-500 via-cyan-500 to-teal-500",
          popularity: 85
        },
        {
          id: "generador-metatags",
          title: "Generador Meta Tags IA",
          description: "Genera meta tags optimizados usando inteligencia artificial avanzada",
          icon: FileText,
          features: ["Generación IA", "Optimización auto", "Múltiples formatos"],
          premium: false,
          category: "optimization",
          gradient: "from-purple-500 via-violet-500 to-indigo-500",
          popularity: 92,
          isNew: true
        },
        {
          id: "analizador-url",
          title: "Analizador URLs SEO",
          description: "Evalúa la estructura SEO de tus URLs con score y recomendaciones",
          icon: Globe,
          features: ["Análisis completo", "Score SEO", "Recomendaciones"],
          premium: false,
          category: "analysis",
          gradient: "from-emerald-500 via-green-500 to-lime-500",
          popularity: 78,
          isFeatured: true
        },
        {
          id: "densidad-palabras",
          title: "Análisis Keywords",
          description: "Calcula densidad y optimización de palabras clave con IA",
          icon: Search,
          features: ["Densidad óptima", "Sugerencias IA", "Análisis semántico"],
          premium: false,
          category: "content",
          gradient: "from-amber-500 via-orange-500 to-red-500",
          popularity: 88
        },
        {
          id: "generador-sitemap",
          title: "Generador Sitemap",
          description: "Crea sitemaps XML optimizados para motores de búsqueda",
          icon: Map,
          features: ["XML automático", "Prioridades", "Frecuencia update"],
          premium: false,
          category: "optimization",
          gradient: "from-pink-500 via-rose-500 to-red-500",
          popularity: 75
        },
        {
          id: "corrector-ortografia",
          title: "Corrector IA",
          description: "Revisa gramática y ortografía con sugerencias inteligentes",
          icon: SpellCheck2,
          features: ["Corrección IA", "Multi-idioma", "Mejoras estilo"],
          premium: true,
          category: "content",
          gradient: "from-yellow-400 via-amber-500 to-orange-500",
          popularity: 82
        },
        {
          id: "verificador-blacklist",
          title: "Verificador Blacklist",
          description: "Verifica si tu dominio está en blacklists o penalizado",
          icon: Shield,
          features: ["Detección blacklist", "Reputación servidor", "Recuperación"],
          premium: false,
          category: "analysis",
          gradient: "from-red-500 via-rose-500 to-pink-500",
          popularity: 70
        },
        {
          id: "analizador-seo-local-gmb",
          title: "Analizador SEO Local",
          description: "Análisis completo de SEO local y Google My Business",
          icon: Building2,
          features: ["SEO Local", "Google My Business", "Análisis NAP"],
          premium: false,
          category: "local",
          gradient: "from-cyan-500 via-blue-500 to-indigo-500",
          popularity: 90,
          isNew: true
        },
        {
          id: "analizador-penalizaciones-google",
          title: "Detector Penalizaciones",
          description: "Detecta y analiza penalizaciones de Google con plan de recuperación",
          icon: Shield,
          features: ["Detección penalties", "Análisis riesgos", "Plan recuperación"],
          premium: false,
          category: "analysis",
          gradient: "from-slate-500 via-gray-600 to-zinc-700",
          popularity: 76
        }
      ],
      aiTools: [
        {
          id: "generador-contenido",
          title: "Generador Contenido IA",
          description: "Crea contenido único y optimizado para SEO con inteligencia artificial",
          icon: PenTool,
          features: ["Contenido original", "SEO optimizado", "Múltiples formatos"],
          premium: false,
          external: false,
          category: "content",
          gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
          popularity: 95,
          isFeatured: false
        },
        {
          id: "chatbot-personalizado",
          title: "Chatbot Personalizado",
          description: "Crea chatbots inteligentes para tu sitio web y atención al cliente",
          icon: MessageSquare,
          features: ["Respuestas auto", "Integración web", "Análisis conversacional"],
          premium: false,
          external: false,
          category: "content",
          gradient: "from-blue-500 via-indigo-500 to-violet-500",
          popularity: 88
        },
        {
          id: "generador-imagenes",
          title: "Generador Imágenes IA",
          description: "Crea imágenes profesionales y únicas con inteligencia artificial",
          icon: Image,
          features: ["Alta resolución", "Estilos variados", "Uso comercial"],
          premium: true,
          external: false,
          category: "content",
          gradient: "from-pink-500 via-rose-500 to-red-500",
          popularity: 92,
          isNew: true
        },
        {
          id: "asistente-codigo",
          title: "Asistente Código IA",
          description: "Genera y optimiza código para desarrollo web y aplicaciones",
          icon: Code,
          features: ["Multi-lenguaje", "Optimización auto", "Debug inteligente"],
          premium: true,
          external: false,
          category: "content",
          gradient: "from-emerald-500 via-teal-500 to-cyan-500",
          popularity: 85
        },
        {
          id: "analizador-sentimientos",
          title: "Analizador Sentimientos",
          description: "Analiza el tono y sentimiento de textos y comentarios",
          icon: Brain,
          features: ["Análisis emocional", "Métricas detalladas", "Reportes visuales"],
          premium: true,
          external: false,
          category: "analysis",
          gradient: "from-amber-500 via-yellow-500 to-lime-500",
          popularity: 78
        },
        {
          id: "chatgpt",
          title: "ChatGPT",
          description: "El chatbot más avanzado para conversaciones y tareas complejas",
          icon: Bot,
          features: ["Conversaciones naturales", "Tareas múltiples", "Contexto avanzado"],
          premium: false,
          external: true,
          url: "https://chatgpt.com/",
          category: "external",
          gradient: "from-green-500 via-emerald-500 to-teal-500",
          popularity: 99
        },
        {
          id: "midjourney",
          title: "Midjourney",
          description: "Generador de imágenes artísticas con IA líder en el mercado",
          icon: Wand2,
          features: ["Arte digital", "Calidad premium", "Estilos únicos"],
          premium: true,
          external: true,
          url: "https://www.midjourney.com/",
          category: "external",
          gradient: "from-indigo-500 via-purple-500 to-pink-500",
          popularity: 96
        },
        {
          id: "claude",
          title: "Claude",
          description: "IA conversacional avanzada de Anthropic para análisis profundo",
          icon: Sparkles,
          features: ["Análisis complejo", "Razonamiento avanzado", "Seguridad mejorada"],
          premium: false,
          external: true,
          url: "https://claude.ai/",
          category: "external",
          gradient: "from-orange-500 via-amber-500 to-yellow-500",
          popularity: 94
        },
        {
          id: "perplexity",
          title: "Perplexity",
          description: "Motor de búsqueda con IA que proporciona respuestas precisas",
          icon: Search,
          features: ["Búsqueda inteligente", "Fuentes citadas", "Respuestas precisas"],
          premium: false,
          external: true,
          url: "https://www.perplexity.ai/",
          category: "external",
          gradient: "from-cyan-500 via-blue-500 to-indigo-500",
          popularity: 91
        },
        {
          id: "grammarly",
          title: "Grammarly",
          description: "Asistente de escritura con IA para mejorar tu redacción",
          icon: FileText,
          features: ["Corrección gramatical", "Mejora estilo", "Tono personalizado"],
          premium: false,
          external: true,
          url: "https://www.grammarly.com/",
          category: "external",
          gradient: "from-green-400 via-emerald-500 to-teal-500",
          popularity: 89
        }
      ]
    },
    en: {
      hero: {
        badge: "Professional Tools Suite",
        title: "Power Your Digital Strategy",
        subtitle: "Enterprise-level SEO and AI tools to dominate search engines",
        stats: [
          { value: "15", label: "Tools" },
          { value: "50K+", label: "Analyses done" },
          { value: "98%", label: "Satisfaction" }
        ]
      },
      tabs: {
        seo: { label: "SEO Tools", icon: Search, count: 10 },
        ai: { label: "AI Tools", icon: Sparkles, count: 10 }
      },
      categories: {
        all: "All",
        analysis: "Analysis",
        optimization: "Optimization",
        local: "Local SEO",
        content: "Content",
        external: "External"
      },
      search: "Search tools...",
      labels: {
        features: "Features",
        premium: "Premium",
        free: "Free",
        external: "External",
        useTool: "Use Tool",
        visitTool: "Visit",
        comingSoon: "Coming Soon",
        new: "New",
        featured: "Featured",
        popular: "Popular"
      },
      seoTools: [
        {
          id: "seo-mega-tool",
          title: "SEO MEGA TOOL",
          description: "Complete SEO audit with competitive analysis, Core Web Vitals and backlinks",
          icon: Rocket,
          features: ["Complete audit", "Competitive analysis", "Core Web Vitals", "Backlinks"],
          premium: false,
          category: "analysis",
          gradient: "from-brand via-red-500 to-orange-500",
          popularity: 98,
          isFeatured: false
        },
        {
          id: "word-counter",
          title: "Word Counter",
          description: "Detailed analysis with readability metrics and reading time",
          icon: Calculator,
          features: ["Readability analysis", "Reading time", "Word density"],
          premium: false,
          category: "content",
          gradient: "from-blue-500 via-cyan-500 to-teal-500",
          popularity: 85
        },
        {
          id: "meta-generator",
          title: "AI Meta Tags Generator",
          description: "Generate optimized meta tags using advanced artificial intelligence",
          icon: FileText,
          features: ["AI generation", "Auto optimization", "Multiple formats"],
          premium: false,
          category: "optimization",
          gradient: "from-purple-500 via-violet-500 to-indigo-500",
          popularity: 92,
          isNew: true
        },
        {
          id: "url-analyzer",
          title: "SEO URL Analyzer",
          description: "Evaluate your URLs SEO structure with score and recommendations",
          icon: Globe,
          features: ["Complete analysis", "SEO score", "Recommendations"],
          premium: false,
          category: "analysis",
          gradient: "from-emerald-500 via-green-500 to-lime-500",
          popularity: 78,
          isFeatured: true
        },
        {
          id: "keyword-density",
          title: "Keyword Analysis",
          description: "Calculate keyword density and optimization with AI",
          icon: Search,
          features: ["Optimal density", "AI suggestions", "Semantic analysis"],
          premium: false,
          category: "content",
          gradient: "from-amber-500 via-orange-500 to-red-500",
          popularity: 88
        },
        {
          id: "sitemap-generator",
          title: "Sitemap Generator",
          description: "Create optimized XML sitemaps for search engines",
          icon: Map,
          features: ["Automatic XML", "Priorities", "Update frequency"],
          premium: false,
          category: "optimization",
          gradient: "from-pink-500 via-rose-500 to-red-500",
          popularity: 75
        },
        {
          id: "spell-checker",
          title: "AI Spell Checker",
          description: "Review grammar and spelling with intelligent suggestions",
          icon: SpellCheck2,
          features: ["AI correction", "Multi-language", "Style improvements"],
          premium: true,
          category: "content",
          gradient: "from-yellow-400 via-amber-500 to-orange-500",
          popularity: 82
        },
        {
          id: "blacklist-checker",
          title: "Blacklist Checker",
          description: "Check if your domain is blacklisted or penalized",
          icon: Shield,
          features: ["Blacklist detection", "Server reputation", "Recovery"],
          premium: false,
          category: "analysis",
          gradient: "from-red-500 via-rose-500 to-pink-500",
          popularity: 70
        },
        {
          id: "local-seo-analyzer",
          title: "Local SEO Analyzer",
          description: "Complete Local SEO and Google My Business analysis",
          icon: Building2,
          features: ["Local SEO", "Google My Business", "NAP Analysis"],
          premium: false,
          category: "local",
          gradient: "from-cyan-500 via-blue-500 to-indigo-500",
          popularity: 90,
          isNew: true
        },
        {
          id: "penalty-detector",
          title: "Penalty Detector",
          description: "Detect and analyze Google penalties with recovery plan",
          icon: Shield,
          features: ["Penalty detection", "Risk analysis", "Recovery plan"],
          premium: false,
          category: "analysis",
          gradient: "from-slate-500 via-gray-600 to-zinc-700",
          popularity: 76
        }
      ],
      aiTools: [
        {
          id: "content-generator",
          title: "AI Content Generator",
          description: "Create unique and SEO-optimized content with artificial intelligence",
          icon: PenTool,
          features: ["Original content", "SEO optimized", "Multiple formats"],
          premium: false,
          external: false,
          category: "content",
          gradient: "from-violet-500 via-purple-500 to-fuchsia-500",
          popularity: 95,
          isFeatured: false
        },
        {
          id: "custom-chatbot",
          title: "Custom Chatbot",
          description: "Create intelligent chatbots for your website and customer service",
          icon: MessageSquare,
          features: ["Auto responses", "Web integration", "Conversational analysis"],
          premium: false,
          external: false,
          category: "content",
          gradient: "from-blue-500 via-indigo-500 to-violet-500",
          popularity: 88
        },
        {
          id: "image-generator",
          title: "AI Image Generator",
          description: "Create professional and unique images with artificial intelligence",
          icon: Image,
          features: ["High resolution", "Various styles", "Commercial use"],
          premium: true,
          external: false,
          category: "content",
          gradient: "from-pink-500 via-rose-500 to-red-500",
          popularity: 92,
          isNew: true
        },
        {
          id: "code-assistant",
          title: "AI Code Assistant",
          description: "Generate and optimize code for web development and applications",
          icon: Code,
          features: ["Multi-language", "Auto optimization", "Smart debugging"],
          premium: true,
          external: false,
          category: "content",
          gradient: "from-emerald-500 via-teal-500 to-cyan-500",
          popularity: 85
        },
        {
          id: "sentiment-analyzer",
          title: "Sentiment Analyzer",
          description: "Analyze the tone and sentiment of texts and comments",
          icon: Brain,
          features: ["Emotional analysis", "Detailed metrics", "Visual reports"],
          premium: true,
          external: false,
          category: "analysis",
          gradient: "from-amber-500 via-yellow-500 to-lime-500",
          popularity: 78
        },
        {
          id: "chatgpt",
          title: "ChatGPT",
          description: "The most advanced chatbot for conversations and complex tasks",
          icon: Bot,
          features: ["Natural conversations", "Multiple tasks", "Advanced context"],
          premium: false,
          external: true,
          url: "https://chatgpt.com/",
          category: "external",
          gradient: "from-green-500 via-emerald-500 to-teal-500",
          popularity: 99
        },
        {
          id: "midjourney",
          title: "Midjourney",
          description: "Market-leading AI artistic image generator",
          icon: Wand2,
          features: ["Digital art", "Premium quality", "Unique styles"],
          premium: true,
          external: true,
          url: "https://www.midjourney.com/",
          category: "external",
          gradient: "from-indigo-500 via-purple-500 to-pink-500",
          popularity: 96
        },
        {
          id: "claude",
          title: "Claude",
          description: "Advanced conversational AI from Anthropic for deep analysis",
          icon: Sparkles,
          features: ["Complex analysis", "Advanced reasoning", "Enhanced security"],
          premium: false,
          external: true,
          url: "https://claude.ai/",
          category: "external",
          gradient: "from-orange-500 via-amber-500 to-yellow-500",
          popularity: 94
        },
        {
          id: "perplexity",
          title: "Perplexity",
          description: "AI-powered search engine that provides accurate answers",
          icon: Search,
          features: ["Smart search", "Cited sources", "Precise answers"],
          premium: false,
          external: true,
          url: "https://www.perplexity.ai/",
          category: "external",
          gradient: "from-cyan-500 via-blue-500 to-indigo-500",
          popularity: 91
        },
        {
          id: "grammarly",
          title: "Grammarly",
          description: "AI writing assistant to improve your writing",
          icon: FileText,
          features: ["Grammar correction", "Style improvement", "Custom tone"],
          premium: false,
          external: true,
          url: "https://www.grammarly.com/",
          category: "external",
          gradient: "from-green-400 via-emerald-500 to-teal-500",
          popularity: 89
        }
      ]
    }
  };

  const t = content[language];
  const currentTools = activeTab === 'seo' ? t.seoTools : t.aiTools;

  // Filter tools based on search and category
  const filteredTools = useMemo(() => {
    return currentTools.filter(tool => {
      const matchesSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           tool.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [currentTools, searchQuery, selectedCategory]);

  // Get featured tool
  const featuredTool = currentTools.find(tool => tool.isFeatured);

  const handleToolClick = (tool: Tool) => {
    if (tool.external && tool.url) {
      window.open(tool.url, '_blank', 'noopener,noreferrer');
    } else if (tool.premium) {
      return;
    } else {
      // Route based on tool id
      const routeMap: Record<string, string> = {
        'seo-mega-tool': '/herramientas/seo-mega-tool',
        'contador-palabras': '/herramientas/contador-palabras',
        'word-counter': '/en/tools/word-counter',
        'generador-metatags': '/herramientas/generador-metatags',
        'meta-generator': '/en/tools/meta-tags-generator',
        'analizador-url': '/herramientas/analizador-url',
        'url-analyzer': '/en/tools/url-analyzer',
        'densidad-palabras': '/herramientas/densidad-palabras',
        'keyword-density': '/en/tools/keyword-density',
        'generador-sitemap': '/herramientas/generador-sitemap',
        'sitemap-generator': '/en/tools/sitemap-generator',
        'verificador-blacklist': '/herramientas/verificador-blacklist',
        'blacklist-checker': '/en/tools/blacklist-checker',
        'analizador-seo-local-gmb': '/herramientas/analizador-seo-local-gmb',
        'local-seo-analyzer': '/en/tools/local-seo-gmb-analyzer',
        'analizador-penalizaciones-google': '/herramientas/analizador-penalizaciones-google',
        'penalty-detector': '/en/tools/google-penalization-analyzer',
        'generador-contenido': '/herramientas/generador-contenido',
        'content-generator': '/en/tools/ai-content-generator',
        'chatbot-personalizado': '/herramientas/chatbot-personalizado',
        'custom-chatbot': '/en/tools/custom-chatbot'
      };

      const route = routeMap[tool.id];
      if (route) {
        navigate(route);
      }
    }
  };

  const ToolCard = ({ tool, index }: { tool: Tool; index: number }) => {
    const isHovered = hoveredTool === tool.id;
    const IconComponent = tool.icon;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        onMouseEnter={() => setHoveredTool(tool.id)}
        onMouseLeave={() => setHoveredTool(null)}
        className="group relative"
      >
        <Card className={`relative overflow-hidden border-slate-700/50 bg-slate-800/50 backdrop-blur-sm transition-all duration-500 h-full ${
          isHovered ? 'border-slate-600/80 shadow-2xl shadow-slate-900/50 scale-[1.02]' : ''
        }`}>
          {/* Gradient border on hover */}
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`absolute inset-0 bg-gradient-to-br ${tool.gradient} opacity-5`}
            />
          )}

          {/* Status badges */}
          <div className="absolute top-3 right-3 flex gap-2 z-10">
            {tool.isNew && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] px-2 py-0.5">
                <Zap className="w-3 h-3 mr-1" />
                {t.labels.new}
              </Badge>
            )}
            {tool.premium ? (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] px-2 py-0.5">
                <Crown className="w-3 h-3 mr-1" />
                {t.labels.premium}
              </Badge>
            ) : (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] px-2 py-0.5">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t.labels.free}
              </Badge>
            )}
            {tool.external && (
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] px-2 py-0.5">
                <ExternalLink className="w-3 h-3 mr-1" />
                {t.labels.external}
              </Badge>
            )}
          </div>

          <CardContent className="p-5 pt-12">
            {/* Icon with gradient background */}
            <div className="mb-4">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tool.gradient} p-0.5 shadow-lg`}
              >
                <div className="w-full h-full rounded-[10px] bg-slate-900 flex items-center justify-center">
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
              </motion.div>
            </div>

            {/* Title and description */}
            <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-300 transition-all duration-300">
              {tool.title}
            </h3>
            <p className="text-sm text-slate-400 mb-4 line-clamp-2">
              {tool.description}
            </p>

            {/* Features */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {tool.features.slice(0, 3).map((feature, i) => (
                <span
                  key={i}
                  className="text-[10px] px-2 py-1 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/30"
                >
                  {feature}
                </span>
              ))}
            </div>

            {/* Popularity bar */}
            {tool.popularity && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {t.labels.popular}
                  </span>
                  <span>{tool.popularity}%</span>
                </div>
                <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: isHovered ? `${tool.popularity}%` : '0%' }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className={`h-full bg-gradient-to-r ${tool.gradient}`}
                  />
                </div>
              </div>
            )}

            {/* Action button */}
            <Button
              onClick={() => handleToolClick(tool)}
              className={`w-full group/btn relative overflow-hidden ${
                tool.premium 
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' 
                  : `bg-gradient-to-r ${tool.gradient} hover:opacity-90 text-white`
              }`}
              disabled={tool.premium && !tool.external}
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {tool.premium && !tool.external ? (
                  <>
                    <Lock className="w-4 h-4" />
                    {t.labels.comingSoon}
                  </>
                ) : tool.external ? (
                  <>
                    {t.labels.visitTool}
                    <ExternalLink className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </>
                ) : (
                  <>
                    {t.labels.useTool}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </>
                )}
              </span>
            </Button>
          </CardContent>

          {/* Border beam effect for featured */}
          {tool.isFeatured && <BorderBeam size={200} duration={12} colorFrom="#ef4444" colorTo="#f97316" />}
        </Card>
      </motion.div>
    );
  };

  const FeaturedToolCard = ({ tool }: { tool: Tool }) => {
    const IconComponent = tool.icon;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative col-span-full"
      >
        <Card className="relative overflow-hidden border-slate-700/50 bg-gradient-to-br from-slate-800/80 via-slate-800/50 to-slate-900/80 backdrop-blur-xl">
          <BorderBeam size={300} duration={10} colorFrom="#ef4444" colorTo="#f97316" />
          
          <CardContent className="p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Content side */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="bg-brand/20 text-brand border-brand/30">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    {t.labels.featured}
                  </Badge>
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t.labels.free}
                  </Badge>
                </div>

                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tool.gradient} p-0.5 shadow-xl mb-6`}>
                  <div className="w-full h-full rounded-[14px] bg-slate-900 flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                </div>

                <h3 className="text-3xl font-bold text-white mb-3">
                  {tool.title}
                </h3>
                <p className="text-lg text-slate-300 mb-6">
                  {tool.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {tool.features.map((feature, i) => (
                    <span
                      key={i}
                      className="text-sm px-3 py-1.5 rounded-full bg-slate-700/50 text-slate-200 border border-slate-600/30 flex items-center gap-1.5"
                    >
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                      {feature}
                    </span>
                  ))}
                </div>

                <Button
                  onClick={() => handleToolClick(tool)}
                  size="lg"
                  className={`bg-gradient-to-r ${tool.gradient} hover:opacity-90 text-white shadow-xl`}
                >
                  {t.labels.useTool}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              {/* Visual side */}
              <div className="relative hidden lg:block">
                <div className="absolute inset-0 bg-gradient-to-br from-brand/20 via-transparent to-orange-500/20 rounded-2xl blur-3xl" />
                <div className="relative bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6">
                  <div className="space-y-4">
                    {/* Mock dashboard preview */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      </div>
                      <span className="text-xs text-slate-500">SEO Analysis Dashboard</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: "SEO Score", value: "92/100", color: "text-emerald-400" },
                        { label: "Core Web Vitals", value: "Good", color: "text-green-400" },
                        { label: "Backlinks", value: "1,234", color: "text-blue-400" }
                      ].map((stat, i) => (
                        <div key={i} className="bg-slate-900/50 rounded-lg p-3 text-center">
                          <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
                          <div className="text-xs text-slate-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[85, 72, 94, 68].map((width, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-24 text-xs text-slate-500">
                            {['On-Page', 'Technical', 'Content', 'Links'][i]}
                          </div>
                          <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${width}%` }}
                              transition={{ duration: 1, delay: i * 0.1 }}
                              className={`h-full bg-gradient-to-r ${tool.gradient}`}
                            />
                          </div>
                          <span className="text-xs text-slate-400">{width}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <section className="relative min-h-screen bg-slate-950 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <GridPattern className="opacity-[0.02]" />
        <FloatingOrb size="xl" color="brand" className="top-20 right-[10%] opacity-20" delay={0} />
        <FloatingOrb size="lg" color="accent" className="bottom-40 left-[5%] opacity-15" delay={2} />
        <FloatingOrb size="md" color="gold" className="top-1/2 right-[20%] opacity-10" delay={4} />
        
        {/* Gradient overlays */}
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-brand/5 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-96 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="mb-6 bg-brand/10 text-brand border-brand/20 px-4 py-2">
            <Rocket className="w-4 h-4 mr-2" />
            {t.hero.badge}
          </Badge>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">{t.hero.title.split(' ')[0]} </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand via-orange-500 to-amber-500">
              {t.hero.title.split(' ').slice(1).join(' ')}
            </span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            {t.hero.subtitle}
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {t.hero.stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand to-orange-500">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8"
        >
          {/* Tabs */}
          <div className="flex bg-slate-800/50 backdrop-blur-sm rounded-xl p-1.5 border border-slate-700/50">
            {Object.entries(t.tabs).map(([key, tab]) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as 'seo' | 'ai')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-brand to-orange-500 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab.label}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20' : 'bg-slate-700'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search and view toggle */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64 bg-slate-800/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-brand/50"
              />
            </div>
            <div className="flex bg-slate-800/50 rounded-lg p-1 border border-slate-700/50">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-brand text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-brand text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                <LayoutList className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Category filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-wrap gap-2 mb-8"
        >
          {Object.entries(t.categories).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedCategory === key
                  ? 'bg-brand text-white'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              {label}
            </button>
          ))}
        </motion.div>

        {/* Tools Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-${selectedCategory}-${searchQuery}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Featured tool */}
            {featuredTool && selectedCategory === 'all' && !searchQuery && (
              <div className="mb-8">
                <FeaturedToolCard tool={featuredTool} />
              </div>
            )}

            {/* Tools grid */}
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                : 'grid-cols-1'
            }`}>
              {filteredTools
                .filter(tool => !tool.isFeatured || selectedCategory !== 'all' || searchQuery)
                .map((tool, index) => (
                  <ToolCard key={tool.id} tool={tool} index={index} />
                ))}
            </div>

            {/* Empty state */}
            {filteredTools.length === 0 && (
              <div className="text-center py-20">
                <Search className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {language === 'es' ? 'No se encontraron herramientas' : 'No tools found'}
                </h3>
                <p className="text-slate-400">
                  {language === 'es' 
                    ? 'Intenta ajustar tu búsqueda o filtros' 
                    : 'Try adjusting your search or filters'}
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Dedicated tool pages (internal linking — Fase 3) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-20"
        >
          <h2 className="text-2xl font-bold text-white mb-2">
            {language === 'es' ? 'Herramientas con guía de uso' : 'Tools with usage guidance'}
          </h2>
          <p className="text-slate-400 mb-6 max-w-2xl">
            {language === 'es'
              ? 'Cada una explica qué mide, qué no mide y cómo interpretar el resultado.'
              : 'Each one explains what it measures, what it does not, and how to read the result.'}
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {(language === 'es'
              ? [
                  { to: '/herramientas/contador-palabras', label: 'Contador de palabras' },
                  { to: '/herramientas/analizador-url', label: 'Analizador de URLs' },
                  { to: '/herramientas/densidad-palabras', label: 'Densidad de palabras clave' },
                  { to: '/herramientas/generador-metatags', label: 'Generador de meta tags' },
                ]
              : [
                  { to: '/en/tools/word-counter', label: 'Word counter' },
                  { to: '/en/tools/url-analyzer', label: 'URL analyzer' },
                  { to: '/en/tools/keyword-density', label: 'Keyword density checker' },
                  { to: '/en/tools/meta-tags-generator', label: 'Meta tags generator' },
                ]
            ).map((t) => (
              <li key={t.to}>
                <Link
                  to={t.to}
                  className="flex items-center justify-between rounded-lg border border-slate-700/50 bg-slate-800/40 px-4 py-3 text-slate-200 transition-colors hover:border-brand/50 hover:text-white"
                >
                  {t.label}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20"
        >
          <Card className="relative overflow-hidden border-slate-700/50 bg-gradient-to-br from-slate-800/80 via-brand/5 to-slate-900/80 backdrop-blur-xl">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {language === 'es' 
                  ? '¿Necesitas una estrategia SEO personalizada?' 
                  : 'Need a customized SEO strategy?'}
              </h2>
              <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
                {language === 'es'
                  ? 'Nuestro equipo de expertos puede ayudarte a dominar los resultados de búsqueda con una estrategia a medida.'
                  : 'Our team of experts can help you dominate search results with a tailored strategy.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={language === 'es' ? '/diagnostico-seo-gratis' : '/en/free-seo-diagnosis'}>
                  <Button size="lg" className="bg-gradient-to-r from-brand to-orange-500 hover:opacity-90 text-white">
                    {language === 'es' ? 'Reunión inicial gratuita' : 'Free initial consultation'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to={language === 'es' ? '/contacto' : '/en/contact'}>
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                    {language === 'es' ? 'Contactar Experto' : 'Contact Expert'}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </Link>
              </div>

            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
